import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'

// 조직 다중 선택 컴포넌트
// - /orgs 목록을 읽어 필터링/체크박스 선택 지원
// - collapsible=true 시 접기/펼치기 토글 제공
export default function OrgMultiSelect({
    value = [],
    onChange,
    placeholder = '조직 검색...',
    className = '',
    collapsible = true,
    defaultOpen = false,
    title = '대상 조직',
    filterType,
    scope = 'all', // 'all' | 'mine'
}) {
    const [options, setOptions] = useState([])
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(defaultOpen)

    useEffect(() => {
        let mounted = true
            ; (async () => {
                setLoading(true)
                try {
                    // orgs/options 사용: scope 파라미터로 내 조직 범위 또는 전체 제어
                    const res = await api.get('/orgs/options', { params: { scope } })
                    const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                    const raw = Array.isArray(payload) ? payload : []
                    let opts = raw.map(o => ({ id: o.id, name: o.name || o.orgName || `조직 ${o.id}`, type: o.type }))
                    if (filterType) {
                        opts = opts.filter(o => (o.type || '').toUpperCase() === String(filterType).toUpperCase())
                    }
                    if (mounted) setOptions(opts)
                } catch (_) {
                    if (mounted) setOptions([])
                } finally {
                    if (mounted) setLoading(false)
                }
            })()
        return () => { mounted = false }
    }, [filterType, scope])

    const filtered = useMemo(() => {
        if (!search.trim()) return options
        const q = search.trim().toLowerCase()
        return options.filter(o => (o.name || '').toLowerCase().includes(q))
    }, [options, search])

    const toggle = (id) => {
        if (!onChange) return
        if (value.includes(id)) onChange(value.filter(v => v !== id))
        else onChange([...value, id])
    }

    return (
        <div className={className}>
            {/* 헤더: 접기/펼치기 토글 */}
            {collapsible && (
                <button
                    type="button"
                    className="w-full flex items-center justify-between rounded border px-3 py-2 text-sm bg-white"
                    onClick={() => setOpen(o => !o)}
                    aria-expanded={open}
                >
                    <span className="font-medium">{title}</span>
                    <span className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">선택 {value.length}개</span>
                        <span className="text-slate-500">{open ? '▼' : '▶'}</span>
                    </span>
                </button>
            )}

            {/* 검색 입력 */}
            {(!collapsible || open) && (
                <div className="flex items-center gap-2 mb-2 mt-2">
                    <input
                        className="rounded border px-3 py-2 text-sm w-full"
                        placeholder={placeholder}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {loading && <span className="text-xs text-slate-500">불러오는 중…</span>}
                </div>
            )}

            {/* 옵션 리스트 */}
            {(!collapsible || open) && (
                <div className="border rounded max-h-48 overflow-y-auto p-2 space-y-1">
                    {filtered.length === 0 && (
                        <div className="text-xs text-slate-500 px-1 py-2">결과 없음</div>
                    )}
                    {filtered.map(org => (
                        <label key={org.id} className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={value.includes(org.id)}
                                onChange={() => toggle(org.id)}
                            />
                            <span>{org.name}{org.type ? ` (${org.type})` : ''}</span>
                        </label>
                    ))}
                </div>
            )}

            {!collapsible && (
                <div className="text-xs text-slate-500 mt-2">선택: {value.length}개</div>
            )}
        </div>
    )
}
